import { SubmissionError } from 'redux-form';
import { replace } from 'react-router-redux';

import _pick from 'lodash/pick';
import _keys from 'lodash/keys';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _flow from 'lodash/flow';
import _filter from 'lodash/filter';
import _isFinite from 'lodash/isFinite';
import _isString from 'lodash/isString';
import _difference from 'lodash/difference';
import _property from 'lodash/property';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import { actions as parentActions } from '../../../editor';
import {
	createSelectOptionsFromSysEnums,
	createSelectOptionsFromSysEnumsForCountries
} from '../../.././../shared/form-elements/select';
import moment from 'moment';

const defaultInitialState = {
	fileToUpload: null,
	fields: {
		affiliate_id: null,
		nickname: '',
		email: '',
		phone: '',
		birthday: null,
		country_id: null,
		location_id: null,
		skype: '',
		telegram: '',
		fb_messenger: '',
		viber: '',
		whats_app: '',
		forums: '',
		cpa_club: '',
		facebook: '',
		instagram: '',
		vk: '',
		additional_contacts: '',
		contact_source: '',
		traffic_sources: '',
		traffic_quality: '',
		geos: [],
		offers: '',
		leads_quantity: '',
		affiliate_network: '',
		manager_id: null,
		comments: '',
		additional_info: '',
		admin_id: null
	}
};

const EXCLUDED_FIELDS = ['created_at', 'updated_at', 'admin_id', 'id', 'image'];
const REFERENCE_FIELDS = ['affiliate_id', 'manager_id', 'country_id', 'location_id', 'geos'];

const createSelectEnum = ( state, field ) => {
	const entity = _get(state, ['applicantEditor', 'applicant', field]);
	return entity && createSelectOptionsFromSysEnums([{
		id: entity.id || 0,
		email: entity.email ? `${entity.email}(${entity.id})` : ''
	}]);
};

const createSelectEnumCountry = ( state, entity = 'country' ) => {
	const country = _get(state, ['applicantEditor', 'applicant', 'extended_location']);
	return country && createSelectOptionsFromSysEnumsForCountries([{
		id: entity === 'location' ? country.id : country.country_id,
		title: entity === 'location' ? country.name : country.country_name,
		country_code: country.country_code
	}]);
};

const createGeoEnums = state => {
	const geos = _get(state, ['applicantEditor', 'applicant', 'extended_geos']);
	return createSelectOptionsFromSysEnumsForCountries(geos);
};

const prepareFormErrors = errors => {
	const validationsErrors = {};
	_forEach(errors, value => {
		const key = value.dataPath.slice(1);
		validationsErrors[key] = value.message;
	});
	return validationsErrors;
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};

const checkPrimitive = value => _isFinite(value) || _isString(value);

const notEmptyCheck = value => !_isEmpty(value) && checkPrimitive(value);

const separatePrimitive = ( data, isEdit ) => keys =>
	_filter(keys, key => {
		const value = data[key];
		return isEdit ? checkPrimitive(value) : notEmptyCheck(value);
	});

const filterPrimitiveFields = (data, excludedFields, isEdit = false) =>
	_flow(
		_keys,
		separatePrimitive(data, isEdit),
		keys => _difference(keys, excludedFields),
		fields => _pick(data, fields)
	)(data);

const filterReferenceFields = (data, fields) => {
	const pickedFields = _pick(data, fields);
	const resultFields = {};
	_forEach(pickedFields, (value, item) => {
		if (item === 'geos') {
			resultFields.geos = _isNil(value) ? [] : value.map(_property('value'));
			return true;
		}
		const pickedValue = _get(value, [0, 'value']);

		if (!_isNil(pickedValue)) {
			resultFields[item] = pickedValue;
		} else {
			resultFields[item] = '';
		}

	});
	if (!resultFields.location_id && resultFields.country_id) {
		resultFields.location_id = resultFields.country_id;
	}
	delete resultFields.country_id;

	return resultFields;
};

const createGeneralForm = ({
	namespace,
	stateNodeName
}) => {

	const MAIN_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const MAIN_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;

	const MAIN_FORM_UPLOAD_SUCCESS = `${namespace}/${stateNodeName}-upload-success`;
	const MAIN_FORM_UPLOAD_ERROR = `${namespace}/${stateNodeName}-upload-error`;
	const MAIN_FORM_PREPARATION_FILE = `${namespace}/${stateNodeName}-preparation-file-success`;

	const initialState = {
		...defaultInitialState
	};

	const actions = {
		mainFormSubmit: formData => (dispatch, getState) => {
			const { data } = getState().applicantEditor;
			const url = data.id ? `${endpoints.APPLICANTS.BASE}/${data.id}` : endpoints.APPLICANTS.BASE;
			const isEdit = !_isNil(data.id);
			const method = isEdit ? 'put' : 'post';

			const primitiveFields = filterPrimitiveFields(formData, EXCLUDED_FIELDS, isEdit);
			const objectFields = filterReferenceFields(formData, REFERENCE_FIELDS);

			if (primitiveFields.birthday && primitiveFields.birthday.length === 10) {
				primitiveFields.birthday = moment(primitiveFields.birthday, 'DD-MM-YYYY').format('YYYY-MM-DD');
			}
			if (primitiveFields.email) {
				primitiveFields.email = primitiveFields.email.toLowerCase();
			}
			if (_isEmpty(primitiveFields.birthday)) {
				primitiveFields.birthday = null;
			}
			if (primitiveFields.phone && primitiveFields.phone.length < 6) {
				primitiveFields.phone = null;
			}
			if (_isNil(objectFields.affiliate_id) || data.affiliate_id === objectFields.affiliate_id) {
				delete objectFields.affiliate_id;
			}
			if (isEdit && _isEmpty(formData.email)) {
				primitiveFields.email = null;
			}
			if (isEdit && _isEmpty(formData.skype)) {
				primitiveFields.skype = null;
			}
			if (isEdit && _isEmpty(formData.telegram)) {
				primitiveFields.telegram = null;
			}

			const request = http({
				url,
				method,
				actions: [ MAIN_FORM_SUBMIT_SUCCESS, MAIN_FORM_SUBMIT_ERROR ],
				data: {
					...primitiveFields,
					...objectFields
				}
			});

			return request(dispatch)
				.then(action => {
					if (action.type === MAIN_FORM_SUBMIT_SUCCESS) {
						const { fileToUpload } = getState().applicantEditor.mainForm;

						setTimeout(() => {
							if (!isEdit) dispatch(replace(`/applicants`));
						}, 100);

						if (fileToUpload) {
							actions.uploadFile(action.payload.data.id, fileToUpload, dispatch);
						} else {
							dispatch(parentActions.toggleProgressBar());
							dispatch(parentActions.fetchData(action.payload.data.id));
						}
					}
					if (action.type === MAIN_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
						const responseErrors = action.payload.response.data;
						throw new SubmissionError(prepareFormErrors(responseErrors));
					}
				});
		},

		preparationFile: (file, nextAction) => dispatch => {
			const reader = new FileReader();
			const data = new FormData();

			data.append('image', file);

			reader.readAsDataURL(file);
			reader.onloadend = function () {
				nextAction(reader.result);
				dispatch({
					type: MAIN_FORM_PREPARATION_FILE,
					payload: data
				});
			};
		},

		uploadFile: (applicantId, fileToUpload, dispatch) => {
			const request = http({
				url: endpoints.APPLICANTS.UPLOAD_APPLICANT_IMAGE.replace('{id}', applicantId),
				method: 'put',
				actions: [MAIN_FORM_UPLOAD_SUCCESS, MAIN_FORM_UPLOAD_ERROR],
				data: fileToUpload
			});

			dispatch(parentActions.toggleProgressBar());

			request(dispatch)
				.then(action => {
					if (action.type === MAIN_FORM_UPLOAD_SUCCESS) {
						dispatch(parentActions.toggleProgressBar());
						dispatch(parentActions.fetchData(action.payload.data.id));
					}
				});
		},

		resetFields: ( fields, change ) => dispatch => value => {
			_forEach(fields, item => dispatch(change(`${item}`, [{}])));
		},

		changePhone: change => dispatch => value => {
			dispatch(change('phone', value));
		}

	};

	const reducers = {
		[MAIN_FORM_SUBMIT_ERROR]: errorReducerFn,
		[MAIN_FORM_UPLOAD_ERROR]: errorReducerFn,
		[MAIN_FORM_PREPARATION_FILE]: (state, action) => ({
			...state,
			mainForm: {
				...state.mainForm,
				fileToUpload: action.payload
			}
		})
	};

	const selectors = {
		getMainFormData: state => {
			const birthday = _get(state, ['applicantEditor', 'applicant', 'birthday']);

			return {
				initialValues: {
					...state.applicantEditor.data,
					affiliate_id: createSelectEnum(state, 'extended_affiliate'),
					manager_id: createSelectEnum(state, 'extended_manager'),
					country_id: createSelectEnumCountry(state),
					location_id: createSelectEnumCountry(state, 'location'),
					birthday: birthday ? moment(birthday).format('DD-MM-YYYY') : '',
					geos: createGeoEnums(state)
				}
			};
		}

	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

export default createGeneralForm;


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/applicants/components/forms/general/index.js